import React, { useState, useEffect, useMemo } from 'react';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  LinkOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  DownOutlined,
  TeamOutlined,
  EyeOutlined,
  SecurityScanOutlined,
  MoneyCollectOutlined,
  BookOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Layout as AntLayout, Menu, Button, Dropdown, Space, Avatar, message, Typography, Tooltip, Tag } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePermissionStore } from '../stores/permissionStore';
import { useFeatureStore } from '../stores/featureStore';
import { useSystemConfigStore } from '../stores/systemConfigStore';
import { getMenuItemsByUserType, filterMenuByPermissions } from '../config/menu.config';
import { authService } from '../services/auth';
import { billingService, type BillingPlan, type SubscriptionInfo } from '../services/billing';

const { Header, Sider, Content, Footer } = AntLayout;
const { Text } = Typography;

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [plan, setPlan] = useState<BillingPlan | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { permissions, menuPermissions } = usePermissionStore();
  const { fetchFeatures, hasBlacklistFeature, loaded: featuresLoaded } = useFeatureStore();
  const { fetchConfig, featureFlags, loaded: configLoaded } = useSystemConfigStore();
  const promotionBaseUrl = (import.meta.env.VITE_PROMOTION_URL as string | undefined) || 'https://console.ipcloak.ai/register';
  const promotionInviteBase = (import.meta.env.VITE_PROMOTION_INVITE_CODE as string | undefined) || 'FANGHU20';
  const promotionInviteCode = useMemo(() => {
    if (!user) {
      return promotionInviteBase;
    }
    const clientId = user.client_id ?? 0;
    const normalizedId = clientId && clientId > 0 ? clientId : user.id;
    return `${promotionInviteBase}-${normalizedId}`;
  }, [user, promotionInviteBase]);
  const promotionLink = `${promotionBaseUrl}?invite=${encodeURIComponent(promotionInviteCode)}`;
  const promotionLabel = (import.meta.env.VITE_PROMOTION_LABEL as string | undefined) || '推广返佣30%';
  const promotionHint = (import.meta.env.VITE_PROMOTION_HINT as string | undefined) || '推广获得30%充值分成';
  const promotionDomainsEnv = import.meta.env.VITE_PROMOTION_DOMAINS as string | undefined;
  const defaultPromotionDomain = useMemo(() => {
    try {
      const url = new URL(promotionBaseUrl);
      return url.hostname;
    } catch {
      return 'ipcloak.ai';
    }
  }, [promotionBaseUrl]);
  const promotionDomains = promotionDomainsEnv
    ? promotionDomainsEnv.split(',').map(domain => domain.trim()).filter(Boolean)
    : [defaultPromotionDomain];
  const showPromotionButton = typeof window !== 'undefined'
    ? promotionDomains.some(domain => {
        if (!domain) return false;
        const host = window.location.hostname;
        return host === domain || host.endsWith(`.${domain}`);
      })
    : false;

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      message.success('退出登录成功');
      navigate('/login');
    } catch (error) {
      console.error('退出登录失败:', error);
      message.error('退出登录失败');
    }
  };

  // 加载订阅信息
  useEffect(() => {
    const loadSubscription = async () => {
      if (!user) {
        setSubscription(null);
        setPlan(null);
        return;
      }
      // 没有查看权限时不请求，以避免 403 提示
      const hasBillingPermission = permissions?.includes('billing:subscription:read');
      if (!hasBillingPermission && user.user_type !== 'admin') {
        setSubscription(null);
        setPlan(null);
        return;
      }

      try {
        setLoadingSubscription(true);
        const res = await billingService.getCurrentSubscription();
        setSubscription(res.subscription ?? null);
        setPlan(res.plan ?? null);
      } catch (error) {
        console.warn('加载订阅信息失败', error);
        setSubscription(null);
        setPlan(null);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [user, permissions]);

  // 加载客户功能列表
  useEffect(() => {
    if (user && user.user_type !== 'admin') {
      // 客户调用 getMyFeatures，不需要传 clientId
      fetchFeatures();
    }
  }, [user, fetchFeatures]);

  // 加载系统配置（包含功能开关）
  useEffect(() => {
    if (user) {
      fetchConfig();
    }
  }, [user, fetchConfig]);

  // 根据用户类型和权限生成菜单
  const generateMenuItems = () => {
    if (!user) {
      console.log('❌ 用户信息不存在，返回空菜单');
      return [];
    }

    console.log('🔍 生成菜单 - 用户类型:', user.user_type, '权限:', permissions);
    
    const baseMenuItems = getMenuItemsByUserType(user.user_type);
    console.log('📋 基础菜单项:', baseMenuItems);
    
    // 帮助文档菜单只在 ipcloak.ai 域名下显示
    const hostname = window.location.hostname;
    const isIpcloakDomain = hostname === 'console.ipcloak.ai' || hostname.endsWith('.ipcloak.ai');

    // 如果权限还没有加载完成，返回基础菜单项（不进行权限过滤）
    if (!permissions || permissions.length === 0) {
      console.log('⚠️ 权限未加载，返回基础菜单项');
      let filteredBaseMenuItems = baseMenuItems;
      if (!isIpcloakDomain) {
        filteredBaseMenuItems = filteredBaseMenuItems.filter(item => item.key !== 'docs');
      }
      const basicMenuItems = filteredBaseMenuItems.map(item => ({
        key: item.path || item.key,
        icon: getIconComponent(item.icon),
        label: item.label,
        children: item.children?.map(child => ({
          key: child.path || child.key,
          icon: getIconComponent(child.icon),
          label: child.label,
        })),
      }));
      console.log('📋 基础菜单项结果:', basicMenuItems);
      return basicMenuItems;
    }
    
    let filteredMenuItems = filterMenuByPermissions(baseMenuItems, permissions, featureFlags);
    console.log('🔍 过滤后的菜单项:', filteredMenuItems);

    // 非管理员用户：根据功能开通状态过滤菜单
    if (user.user_type !== 'admin' && featuresLoaded) {
      // 如果未开通黑名单功能，隐藏黑名单菜单
      if (!hasBlacklistFeature()) {
        filteredMenuItems = filteredMenuItems.filter(item => item.key !== 'blacklist');
        console.log('🚫 黑名单功能未开通，已隐藏黑名单菜单');
      }
      
      // 如果未开通增值功能，隐藏增值功能菜单
      const { isFeatureEnabled } = useFeatureStore.getState();
      if (!isFeatureEnabled('pixel')) {
        filteredMenuItems = filteredMenuItems.filter(item => item.key !== 'pixel');
        console.log('🚫 增值功能未开通，已隐藏增值功能菜单');
      }
    }

    // 帮助文档菜单只在 ipcloak.ai 域名下显示（域名判断已在上面定义）
    if (!isIpcloakDomain) {
      filteredMenuItems = filteredMenuItems.filter(item => item.key !== 'docs');
    }

    const finalMenuItems = filteredMenuItems.map(item => ({
      key: item.path || item.key,
      icon: getIconComponent(item.icon),
      label: item.label,
      children: item.children?.map(child => ({
        key: child.path || child.key,
        icon: getIconComponent(child.icon),
        label: child.label,
      })),
    }));
    
    console.log('📋 最终菜单项:', finalMenuItems);
    return finalMenuItems;
  };

  // 根据图标名称获取对应的React组件
  const getIconComponent = (iconName?: string) => {
    if (!iconName) return null;
    
    const iconMap: { [key: string]: React.ReactNode } = {
      'DashboardOutlined': <DashboardOutlined />,
      'TeamOutlined': <TeamOutlined />,
      'LinkOutlined': <LinkOutlined />,
      'EyeOutlined': <EyeOutlined />,
      'BarChartOutlined': <BarChartOutlined />,
      'UserOutlined': <UserOutlined />,
      'SettingOutlined': <SettingOutlined />,
      'SecurityScanOutlined': <SecurityScanOutlined />,
      'MoneyCollectOutlined': <MoneyCollectOutlined />,
      'HistoryOutlined': <HistoryOutlined />,
    };
    
    return iconMap[iconName] || null;
  };

  const menuItems = generateMenuItems();
  
  // 备用菜单：如果主菜单为空，提供基本菜单
  const fallbackMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: '/links',
      icon: <LinkOutlined />,
      label: '链接管理',
    },
    {
      key: '/rules',
      icon: <SecurityScanOutlined />,
      label: '拦截规则',
    },
    {
      key: '/visitors',
      icon: <EyeOutlined />,
      label: '访客记录',
    },
    {
      key: '/statistics',
      icon: <BarChartOutlined />,
      label: '统计分析',
    },
  ];
  
  // 使用备用菜单如果主菜单为空
  const finalMenuItems = menuItems.length > 0 ? menuItems : fallbackMenuItems;
  
  // 调试信息：如果菜单为空，显示详细信息
  useEffect(() => {
    console.log('🔍 菜单状态检查:');
    console.log('- 用户信息:', user);
    console.log('- 权限信息:', permissions);
    console.log('- 菜单权限:', menuPermissions);
    console.log('- 生成的菜单项:', menuItems);
    console.log('- 菜单项数量:', menuItems.length);
    console.log('- 最终菜单项:', finalMenuItems);
    console.log('- 最终菜单项数量:', finalMenuItems.length);
  }, [user, permissions, menuPermissions, menuItems, finalMenuItems]);

  const userMenuItems = [
    {
      key: 'profile',
      label: '个人资料',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={240}
        style={{
          background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ 
          height: 64, 
          margin: collapsed ? '12px' : '16px', 
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: collapsed ? '18px' : '20px',
          fontWeight: 'bold',
          borderRadius: '8px',
          transition: 'all 0.3s',
        }}>
          {collapsed ? '访' : '访客管理平台'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={finalMenuItems}
          onClick={({ key }) => navigate(key)}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: '15px',
            fontWeight: 500,
            flex: 1,
          }}
        />
        <div style={{
          padding: collapsed ? '12px 8px' : '12px 16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center',
        }}>
          <a
            href="https://t.me/webpage1688"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '13px',
              textDecoration: 'none',
              display: 'block',
              transition: 'color 0.3s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.65)')}
          >
            {collapsed ? '客服' : '联系客服 @webpage1688'}
          </a>
        </div>
      </Sider>
      <AntLayout>
        <Header style={{ 
          padding: 0, 
          background: '#fff', 
          display: 'flex', 
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '18px',
              width: 64,
              height: 64,
              color: '#666',
            }}
          />
          <div style={{ flex: 1, paddingRight: 16 }}>
            <Space size={12} wrap>
              {user?.user_type === 'admin' && (
                <Button type="link" onClick={() => navigate('/billing')} style={{ padding: 0 }}>计费中心</Button>
              )}
              {loadingSubscription ? (
                <Text type="secondary">套餐加载中...</Text>
              ) : subscription && plan ? (
                <Space size={8} align="center">
                  <Text strong>当前套餐</Text>
                  <Tag color="blue" style={{ marginRight: 0 }}>
                    {plan.display_name || plan.name}
                  </Tag>
                  <Tag color={
                    subscription.status === 'active' ? 'green'
                    : subscription.status === 'trialing' ? 'gold'
                    : subscription.status === 'past_due' ? 'orange'
                    : subscription.status === 'expired' ? 'red'
                    : 'default'
                  } style={{ marginRight: 0 }}>
                    {subscription.status === 'active' && '已激活'}
                    {subscription.status === 'trialing' && '试用中'}
                    {subscription.status === 'past_due' && '逾期'}
                    {subscription.status === 'canceled' && '已取消'}
                    {subscription.status === 'expired' && '已过期'}
                    {subscription.status === 'pending' && '待生效'}
                  </Tag>
                  {subscription.end_date && (
                    <Text type="secondary">
                      有效期至 {new Date(subscription.end_date).toLocaleDateString()}
                    </Text>
                  )}
                  {!subscription.end_date && subscription.trial_end_at && (
                    <Text type="secondary">
                      试用至 {new Date(subscription.trial_end_at).toLocaleDateString()}
                    </Text>
                  )}
                </Space>
              ) : (
                <Text type="secondary">
                  {user?.user_type === 'admin'
                    ? '未开通套餐，点击计费中心了解更多'
                    : plan?.display_name || plan?.name || '免费体验套餐'}
                </Text>
              )}
            </Space>
          </div>
          {showPromotionButton && (
            <Tooltip title={promotionHint} placement="bottom">
              <Button
                type="primary"
                onClick={() => navigate('/referral')}
                icon={<MoneyCollectOutlined />}
                style={{
                  marginRight: 16,
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(234, 88, 12, 0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {promotionLabel}
              </Button>
            </Tooltip>
          )}
          {/* <Button
            type="link"
            href="https://ipcloak.icu"
            target="_blank"
            rel="noopener noreferrer"
            icon={<BookOutlined />}
            style={{ marginRight: 16, fontSize: '15px' }}
          >
            操作文档
          </Button> */}
          <div style={{ marginRight: 24 }}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', transition: 'all 0.3s' }} className="hover:bg-gray-50">
                <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                <span style={{ fontSize: '15px', fontWeight: 500 }}>{user?.username}</span>
                <span style={{ color: '#999', fontSize: '13px' }}>
                  ({user?.user_type === 'admin' ? '系统管理员' : 
                    user?.user_type === 'client' ? '客户账号' : '子账号'})
                </span>
                <DownOutlined style={{ color: '#999' }} />
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '24px',
            padding: 0,
            minHeight: 280,
            background: 'transparent',
          }}
        >
          {children}
        </Content>
        <Footer style={{ 
          textAlign: 'center',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          padding: '16px 50px',
        }}>
          <Text type="secondary" style={{ fontSize: '20px' }}>
            © 2025斗篷  ipcloak.ai 
          </Text>
        </Footer>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;